import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        // This path has always morphed the drawer by hydration key, so it has
        // always been unable to reach the dialog's empty-state class — emptying
        // the cart from the drawer left it un-marked. Same reconcile. (#416)
        this.#syncDrawerEmptyState();
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    // The drawer's section IS the header section (its markup lives in
    // snippets/header-actions.liquid), so a 'full' morph here rebuilds the whole
    // header — re-rendering an open top-layer <dialog> and wiping the gldn header
    // injection (#386). updateQuantity() already morphs the drawer in 'hydration'
    // mode for exactly this reason; the cart:update path was the odd one out.
    // Hydration mode re-morphs the keyed `cart-drawer-inner` subtree, which is
    // the entire drawer body, so nothing is lost. (gldn99 #416)
    const mode = this.isDrawer ? 'hydration' : 'full';

    // morphSection throws when the response doesn't contain the section
    // ('Section <id> not found in the section rendering response'). Unhandled,
    // that leaves the cart frozen on whatever partial markup it had, with no way
    // back short of a page reload — the reported header-only stuck cart. Recover
    // by re-requesting the section once instead of dying silently. (gldn99 #416)
    const recover = (error) => {
      console.error('cart section morph failed; re-requesting section', error);
      return sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode })
        .then(() => this.#updateCartQuantitySelectorButtonStates())
        .catch((retryError) => {
          console.error('cart section re-render failed', retryError);
        });
    };

    const settle = () => {
      this.#syncDrawerEmptyState();
      this.#updateCartQuantitySelectorButtonStates();
    };

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, mode)
        // Update button states for all cart quantity selectors after morph
        .then(settle)
        .catch(recover);
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode })
        .then(settle)
        .catch(recover);
    }
  };

  /**
   * Reconciles the drawer's empty-state class after a morph.
   *
   * `cart-drawer--empty` is stamped server-side on the `<dialog>` from
   * `cart.empty?`, and the dialog sits ABOVE `[data-hydration-key="cart-drawer-inner"]`
   * — so a hydration-mode morph structurally cannot update it. Land on a page
   * with an empty cart, add an item, and the class stays stamped: it centres the
   * whole drawer (`.cart-drawer--empty .cart-drawer__content { text-align: center }`)
   * and flex-collapses `.cart-drawer__inner`. It self-heals only on the next full
   * page render, which is why it looks intermittent.
   *
   * The full-morph path used to clear it as a side effect of rebuilding the whole
   * header. Now that the drawer morphs by hydration key, state above that key has
   * to be reconciled explicitly. Uses the same `.cart-form` signal as the theme's
   * own `.cart-drawer:not(:has(.cart-form))` rule. (gldn99 #416)
   */
  #syncDrawerEmptyState() {
    if (!this.isDrawer) return;

    const dialog = this.closest('dialog');
    if (!dialog) return;

    const isEmpty = !this.querySelector('.cart-form');
    dialog.classList.toggle('cart-drawer--empty', isEmpty);

    // The heading the dialog is labelled by swaps with the same state.
    const headingId = isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading';
    if (dialog.getAttribute('aria-labelledby') !== headingId) {
      dialog.setAttribute('aria-labelledby', headingId);
    }
  }

  /**
   * Controls whose behaviour is keyed to a cart line INDEX, which goes stale as
   * soon as an in-flight mutation lands. These are the only things the in-flight
   * guard has to block — see the `.cart-items-disabled` note in
   * `snippets/cart-products.liquid` (gldn91 #415).
   */
  static #lineKeyedControls = 'button.cart-items__remove-link, button.cart-items__action-remove, quantity-selector-component button, cart-quantity-selector-component button';

  /**
   * Disables the line-mutating cart controls for the duration of a cart write.
   *
   * Deliberately NOT a drawer-wide block: in the drawer this component also
   * wraps `.cart-drawer__summary`, so blocking the whole subtree took the
   * checkout button and every product link down with it (gldn91 #415).
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');

    for (const control of this.querySelectorAll(CartItemsComponent.#lineKeyedControls)) {
      // Remember pre-existing disabled controls (e.g. a quantity "minus" already
      // at its floor) so re-enabling doesn't wrongly turn them back on.
      if (control.disabled) {
        control.dataset.cartItemsWasDisabled = 'true';
      } else {
        control.disabled = true;
      }
    }
  }

  /**
   * Re-enables the line-mutating cart controls after a cart write settles.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');

    for (const control of this.querySelectorAll(CartItemsComponent.#lineKeyedControls)) {
      if (control.dataset.cartItemsWasDisabled) {
        delete control.dataset.cartItemsWasDisabled;
        continue;
      }
      control.disabled = false;
    }

    // A morph between disable and enable swaps in fresh controls, so the
    // `wasDisabled` markers above don't survive it. Re-derive the quantity
    // button states from the current cart rather than trusting the markers —
    // otherwise a "minus" sitting at its floor comes back clickable.
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
